import React from 'react';
import Accordion from '../ui/Accordion';
import { HelpCircle, MessageCircle } from 'lucide-react';
import { Box, Flex, Text } from '@chakra-ui/react';

const faqs = [
  {
    question: 'What is DatLynk?',
    answer: 'DatLynk is a structure-aware sync platform for teams that need Jira and ClickUp to stay aligned. It keeps hierarchies, mapped fields, and updates coordinated without flattening the work or forcing teams into one tool.',
  },
  {
    question: 'What integrations are available now?',
    answer: 'DatLynk is live today for Jira and ClickUp. The platform is built for broader cross-tool sync over time, but the current launch focuses on the Jira and ClickUp workflows teams need right now.',
  },
  {
    question: 'How is DatLynk different from Zapier or Make?',
    answer: 'General automation tools move flat records with triggers and actions. DatLynk is purpose-built for Jira and ClickUp sync, so it preserves hierarchy, mapped fields, and the context around each task instead of creating disconnected items.',
  },
  {
    question: 'How does DatLynk handle conflicting changes?',
    answer: 'DatLynk tracks both sides of the sync and applies your configured direction and mapping rules to keep changes consistent. When the same item changes in both tools, it resolves the update using those rules and keeps the sync state visible in the app.',
  },
  {
    question: 'How do hierarchy and field mappings work?',
    answer: 'DatLynk lets you map the structure and fields that matter between Jira and ClickUp. Epics, tasks, subtasks, statuses, and mapped fields stay coordinated so teams keep the same context on both sides.',
  },
  {
    question: 'How is my data protected?',
    answer: 'DatLynk uses secure OAuth flows, protected credential storage, and controlled server-side exchanges so providers are not authenticated directly from the browser. The goal is to keep sync secure without turning DatLynk into a second system of record.',
  },
  {
    question: 'What is an active sync item?',
    answer: 'An active sync item is a Jira issue or ClickUp task that DatLynk is monitoring and keeping aligned. Pricing is based on the amount of work you actively sync, not on how many seats your team has.',
  },
  {
    question: 'Can I cancel or change my plan?',
    answer: 'Yes. You can upgrade, downgrade, or cancel your plan as your workload changes. DatLynk is designed to scale with the amount of sync you need, not to lock you into a fixed setup.',
  },
];

const FAQ = () => {
  return (
    <Box
      as="section"
      id="faq"
      aria-labelledby="faq-title"
      bg="landing.surface"
      py={{ base: 24, md: 32 }}
      fontFamily="body"
      position="relative"
      overflow="hidden"
    >
      {/* Background Decor */}
      <Box 
        position="absolute" 
        top="-10%" left="-5%" 
        w="40%" h="40%" 
        bg="radial-gradient(circle, rgba(37, 99, 235, 0.03) 0%, transparent 70%)" 
        pointerEvents="none" 
      />
      
      <Box maxW="900px" mx="auto" px={{ base: 5, md: 8 }} position="relative" zIndex={1}>
        {/* Header */}
        <Box textAlign="center" mb={16}>
          <Flex
            display="inline-flex"
            align="center"
            gap={1.5}
            px={3} py={1.5}
            borderRadius="full"
            bg="brand.subtle"
            borderWidth="1px"
            borderColor="landing.border"
            mb={4}
            mx="auto"
            color="brand.primary"
          >
            <HelpCircle size={14} />
            <Text fontSize="xs" fontWeight="700" color="brand.primary" letterSpacing="0.05em">
              F.A.Q.
            </Text>
          </Flex>
          <Text
            as="h2"
            id="faq-title"
            fontSize={{ base: '2xl', md: '5xl' }}
            fontWeight="900"
            color="text.main"
            letterSpacing="-0.03em"
            mb={4}
          >
            Frequently{' '}
            <Box as="span" color="brand.primary" whiteSpace="nowrap">Asked Questions</Box>
          </Text>
          <Text fontSize="lg" color="text.muted" maxW="560px" mx="auto" lineHeight="1.7">
            Everything you need to know about how DatLynk keeps your tools in sync.
          </Text>
        </Box>

        {/* Accordion list */}
        <Box role="list" mb={10}>
          {faqs.map((faq, index) => (
            <Box key={index} role="listitem">
              <Accordion title={faq.question} initialOpen={index === 0}>{faq.answer}</Accordion>
            </Box>
          ))}
        </Box>

        {/* Contact card */}
        <Flex
          align="center"
          gap={4}
          p={6}
          borderRadius="2xl"
          bg="landing.card"
          borderWidth="1px"
          borderColor="landing.border"
          boxShadow="0 2px 16px rgba(27,79,204,0.06)"
          direction={{ base: 'column', sm: 'row' }}
          textAlign={{ base: 'center', sm: 'left' }}
        >
          <Flex
            w="48px"
            h="48px"
            borderRadius="xl"
            bg="brand.subtle"
            align="center"
            justify="center"
            flexShrink={0}
            color="brand.primary"
          >
            <MessageCircle size={22} />
          </Flex>
          <Box flex={1}>
            <Text fontWeight="700" color="text.main" mb={0.5}>Still have questions?</Text>
            <Text fontSize="sm" color="text.muted">We're here to help you get started with your first sync.</Text>
          </Box>
          <Box
            as="a"
            href="mailto:hello@datlynk.com"
            px={6}
            py={2.5}
            borderRadius="lg"
            bg="brand.primary"
            color="white"
            fontSize="sm"
            fontWeight="600"
            border="none"
            cursor="pointer"
            whiteSpace="nowrap"
            transition="all 200ms"
            _hover={{ bg: 'brand.700', transform: 'translateY(-1px)' }}
          >
            Contact Support
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

export default FAQ;
